import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import Api from '../utils/Api';
import { useAuth } from './AuthContext';
import { useError } from './ErrorContext';
import { useLoading } from './LoadingContext';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useRoutePath } from '../utils/getRoutePath';

const ServiceCategoryContext = createContext();
 
export const useServiceCategories = () => {
  const context = useContext(ServiceCategoryContext);
  if (!context) {
    throw new Error('useServiceCategories must be used within a ServiceCategoryProvider');
  }
  return context;
};

export const ServiceCategoryProvider = ({ children }) => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { hideLoading } = useLoading();
  const { getToken } = useAuth();
  const [ setApi] = useState(null);
  const { handleApiError, formErrors, clearErrors } = useError();
  const [serviceCategories, setServiceCategories] = useState([]);
  const segments = location.pathname.split('/');
  const id = segments.pop();
  const [paginate, setPaginate] = useState(true);
  const getRoutePath = useRoutePath();
  const initialized = location.pathname === getRoutePath('/service-categories');

  const getServiceCategories = useCallback(async (page = 1, filters = {}) => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }
   
    try {
      const searchParams = new URLSearchParams(window.location.search);
      let query = '';
      if(filters?.q){
        query = '&q='+filters?.q;
        
      } else{
        const q = searchParams.get('q') || '';
        query = '&q='+q;
      }
      if(filters?.active){
        query += '&active='+filters?.active;
      } else{
        const active = searchParams.get('active') || '';
        query += '&active='+active;
      }
      setPaginate(true)
      let response = await apiInstance.call('service-categories?page=' + page+'&paginate='+paginate +''+query , 'GET');
      if(filters?.all){
        query = '';
        setPaginate(false)
        if(filters?.active){
          if(filters?.active === true){
            query += 'active=1';
          }else{
            query += 'active=0';
          }
         
        }
        response = await apiInstance.call('service-categories?'+query);  
      }
      
      if (response.status === 200) {
       // setPaginate(response.data.paginate)
        setServiceCategories(response.data.serviceCategories || []);
       
        
        if(filters){
          return response.data.serviceCategories;
        }
      } else {
        
        handleApiError(response.error, {
          showAlert: false,
          redirectOn401: true,
          customMessage: 'Failed to fetch service categories information'
        });
      }
    } catch (error) {
     
    } finally {
      
    }
  }, [handleApiError, getToken,paginate]);

  const getServiceCategoryByIdApi = useCallback(async (id, filters = {}) => {
    
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }
    const response = await apiInstance.call(`service-categories/${id}/edit`, 'GET', null, true, false);
   
    if (response.status === 200) {
      
      let serviceCategories = {'data':[response.data.serviceCategory]}
      setServiceCategories(serviceCategories || []);
      
    } 
  }, [getToken]);

  useEffect(() => {
    
    const handleLoad = async() => {
      const currentPath = window.location.pathname;
      
      if(currentPath.includes(getRoutePath('/service-categories/edit/'+id))){
        if (getToken()) {
        
          
          await getServiceCategoryByIdApi(id);
        }
      }
    };

    // Add onload listener
    window.addEventListener('load', handleLoad);

    // Cleanup listener
    return () => {
      window.removeEventListener('load', handleLoad);
    };
  }, [getRoutePath, id, getToken, getServiceCategoryByIdApi, searchParams,setApi]);

  // Initialize API and load initial data
  useEffect(() => {
    const initializeApi = async () => {
      try {
     
        if (getToken()) {
          let page = parseInt(searchParams.get('page')) || 1;
         
          await getServiceCategories(page);
        }
      } catch (error) {
        hideLoading();
      }
    };
   
    if(initialized){
      initializeApi();
    }
  }, [getToken, getServiceCategories, searchParams, initialized,hideLoading,setApi]);

    
  const addServiceCategory = async (data) => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }

    try {

      let formData = new FormData();

      // Append image if provided
      if (data.image) {
        formData.append('image', data.image);
      }
      
      // Append the rest of the data as JSON
      formData.append('data', JSON.stringify(data));

      const response = await apiInstance.call('service-categories', 'POST', formData, true,true);
      
      if (response.status === 201 || response.status === 200) {
        // Refresh the service categories list
        await getServiceCategories();
        return { status: response.status, data: response.data };
      } else {
        if (response.error.status === 422) {
          return {
            status: response.error.status,
            errors: response.error.validationErrors
          };
        } else {
          // Only call handleApiError for non-validation errors
          handleApiError(response.error, {
            showAlert: false,
            redirectOn401: true,
            customMessage: 'Failed to add Service Category'
          });
          return {
            status: response.error.status,
            error: response.error
          };
        }
      }
    } catch (error) {
      handleApiError(error, {
        showAlert: true,
        redirectOn401: true,
        customMessage: 'Failed to create service category'
      });
      return { status: 500, error: error.message };
    }
  };
  
  const getServiceCategoryById = useCallback(async (id) => {
   // let category = serviceCategories?.data?.find(serviceCategory => Number(serviceCategory.id) === Number(id));
 
    //return {'status':200,'data':category};
    
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }

    try {
      const response = await apiInstance.call(`service-categories/${id}`, 'GET', null, true);
      
      if (response.status === 200) {
        return { status: response.status, data: response.data.serviceCategory };
      } else {
        handleApiError(response.error, {
          showAlert: true,
          redirectOn401: true,
          customMessage: 'Failed to fetch service category details'
        });
        return { status: response.status, error: response.error };
      }
    } catch (error) {
      handleApiError(error, {
        showAlert: true,
        redirectOn401: true,
        customMessage: 'Failed to fetch service category details'
      });
      return { status: 500, error: error.message };
    }
  }, [getToken, handleApiError]);

  const updateServiceCategory = async (id, updatedData) => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }

    try {
      let formData = new FormData();

      // Append image if provided
      if (updatedData.image) {
        formData.append('image', updatedData.image);
      }
      
      // Append the rest of the data as JSON
      formData.append('data', JSON.stringify(updatedData));
      
      // Now send the request with formData only once
      const response = await apiInstance.call(`service-categories/${id}`, 'POST', formData, true, true);
      
      if (response.status === 200) {
        // Refresh the service categories list
        await getServiceCategories();
        return { status: response.status, data: response.data };
      } else {
        if (response.error.status === 422) {
          return {
            status: response.error.status,
             errors: response.error.validationErrors,
          };
        } else {
          // Only call handleApiError for non-validation errors
          handleApiError(response.error, {
            showAlert: false,
            redirectOn401: true,
            customMessage: 'Failed to update Service Category'
          });
          return {
            status: response.error.status,
            error: response.error
          };
        }
      }
    } catch (error) {
      handleApiError(error, {
        showAlert: true,
        redirectOn401: true,
        customMessage: 'Failed to update service category'
      });
      return { status: 500, error: error.message };
    }
  };

  const deleteServiceCategory = async (id) => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }

    try {
      const response = await apiInstance.call(`service-categories/${id}`, 'DELETE', null, true);
      
      if (response.status === 200) {
        // Refresh the service categories list
        await getServiceCategories();
        navigate(getRoutePath('/service-categories'));
        return { status: response.status, data: response.data };
      } else {
        handleApiError(response.error, {
          showAlert: true,
          redirectOn401: true,
          customMessage: 'Failed to delete service category'
        });
        return { status: response.status, error: response.error };
      }
    } catch (error) {
      handleApiError(error, {
        showAlert: true,
        redirectOn401: true,
        customMessage: 'Failed to delete service category'
      });
      return { status: 500, error: error.message };
    }
  };

  return (
    <ServiceCategoryContext.Provider
      value={{
        serviceCategories,
        addServiceCategory,
        getServiceCategoryById,
        updateServiceCategory,
        deleteServiceCategory,
        getServiceCategories,
        formErrors,
        clearErrors,
        setPaginate,
        paginate
      }}
    >
      {children}
    </ServiceCategoryContext.Provider>
  );
};

export default ServiceCategoryContext; 